import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Node, useReactFlow, useNodes, useEdges } from 'reactflow';
import { WorkflowBaseService } from '../services/WorkflowBaseService';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "@/shared/components/ui/use-toast";
import { Pencil } from "lucide-react";
import { Loader2 } from "lucide-react";
import { Plus } from "lucide-react";
import { NodeTesting } from './NodeTesting';
import { NodeInput } from './NodeInput';
import { ArrayFieldItemForm } from "./ArrayFieldItemForm"
import { RemoveItemButton } from "./RemoveItemButton"
import { createField } from './fields/FieldFactory';
import type { BaseFieldProps } from './fields/types';
import { generateFieldSchema } from '../utils/schemaGenerator';
import { extractFields } from '../utils/formUtils';

/** Extended Node type to include custom workflow properties */
type WorkflowNode = Node & {
  nodeId?: string;
  config?: Record<string, { value?: unknown; type?: string } | unknown>;
};

interface NodeField {
  name?: string;
  key?: string;
  type?: string;
  label?: string;
  description?: string;
  default?: unknown;
  items?: {
    type?: string;
    fields: Array<{ name: string; default?: unknown; [key: string]: unknown }>;
  };
  visibilityConditions?: unknown;
  [key: string]: unknown;
}

interface NodeDefinition {
  id?: string;
  name?: string;
  type?: string;
  description?: string;
  settings?: Record<string, unknown>;
  workflowConfig?: Record<string, unknown>;
  workflowData?: Record<string, unknown>;
  [key: string]: unknown;
}

interface WorkflowEdge {
  source?: string;
  target?: string;
  input?: unknown;
  [key: string]: unknown;
}

interface NodeConfigDialogProps {
  node: WorkflowNode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NodeConfigDialog({
  node,
  open,
  onOpenChange,
}: NodeConfigDialogProps) {
  const { t } = useTranslation();
  const [nodeDefinition, setNodeDefinition] = useState<NodeDefinition | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [nodeTitle, setNodeTitle] = useState("");
  const [testOutput, setTestOutput] = useState<unknown>(null);
  const [testStatus, setTestStatus] = useState<string | null>(null);
  const [nodeOutput, setNodeOutput] = useState<unknown>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Estado para controlar o salvamento
  const titleInputRef = useRef<HTMLInputElement>(null);
  const { setNodes, getNode, getEdges } = useReactFlow();
  const [inputData, setInputData] = useState<Record<string, unknown>>({});
  const canvasNodes = useNodes();
  const canvasEdges = useEdges();

  // Reactively compute derived input keys from source nodes' canvas config.
  // This recalculates whenever any node/edge in the canvas changes, so renaming
  // a target_parameter in the previous node is reflected immediately.
  const derivedInputKeys = useMemo(() => {
    if (!node) return [];
    const CONFIG_SKIP_KEYS = new Set([
      'title', 'template_slug', 'template_language', 'to', 'cc', 'bcc',
      'nodeId', 'executionId', 'organizationId', 'workspaceId', 'agentBaseId',
      'parameters', 'prompts', '_webhook',
    ]);
    const incomingEdges = canvasEdges.filter(e => e.target === node.id);
    const derivedInputs: Record<string, unknown> = {};
    for (const edge of incomingEdges) {
      const sourceNode = canvasNodes.find(n => n.id === edge.source) as (Node & { config?: Record<string, unknown> }) | undefined;
      if (!sourceNode?.config || typeof sourceNode.config !== 'object') continue;
      const mappingRaw = sourceNode.config['mapping'];
      const mappingArray: unknown[] | null = Array.isArray(mappingRaw)
        ? mappingRaw
        : (mappingRaw && typeof mappingRaw === 'object' && Array.isArray((mappingRaw as Record<string, unknown>)['value']))
          ? (mappingRaw as Record<string, unknown[]>)['value']
          : null;
      if (mappingArray) {
        for (const entry of mappingArray) {
          const e = entry as Record<string, unknown>;
          const target = e['target'] ?? e['target_parameter'];
          if (typeof target === 'string' && target.trim()) {
            derivedInputs[target.trim()] = '';
          }
        }
        continue;
      }
      for (const [k, v] of Object.entries(sourceNode.config)) {
        if (!CONFIG_SKIP_KEYS.has(k)) {
          const val = (v && typeof v === 'object' && 'value' in v) ? (v as { value: unknown }).value : v;
          derivedInputs[k] = val ?? '';
        }
      }
    }
    return Object.keys(derivedInputs);
  }, [canvasNodes, canvasEdges, node]);

  // Atualiza o título inicial quando o node muda
  useEffect(() => {
    if (node) {
      const currentNode = getNode(node.id);
      if (!currentNode) return;

      // Verificar se temos o workflowId
      if (!currentNode.data.workflowId) {
        return;
      }

      setNodeTitle(currentNode.data.title || currentNode.data.label || '');
    }
  }, [node, getNode]);

  const handleSaveTitle = async () => {
    if (!node) return;

    try {
      // Atualizar o título no node (apenas estado local)
      setNodes(nodes => nodes.map(n => {
        if (n.id === node.id) {
          return {
            ...n,
            data: {
              ...n.data,
              title: nodeTitle
            }
          };
        }
        return n;
      }));

      // NÃO salvar automaticamente no banco de dados
      // As alterações serão salvas apenas quando o usuário clicar em "Publicar"
      // O WorkflowEditor notificará o AgentSettings sobre as alterações

      setIsEditingTitle(false);
    } catch (error) {
      console.error('Error updating node title:', error);
      toast({
        variant: "destructive",
        title: t('error').charAt(0).toUpperCase() + t('error').slice(1),
        description: t('agents.workflow.node.title.updateError'),
      });
    }
  };

  const handleTitleEdit = () => {
    setIsEditingTitle(true);
    // Foca no input após renderizar
    setTimeout(() => {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, 0);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
      // Restaura o título original
      const currentNode = getNode(node?.id || '');
      setNodeTitle(currentNode?.data?.title || currentNode?.data?.label || '');
    }
  };

  // Carregar a definição do node quando o diálogo abrir
  useEffect(() => {
    if (node) {
      // Procurar o nodeId em diferentes lugares possíveis
      const nodeId = node.data?.nodeId || // Local padrão
                    node.nodeId || // Alternativa 1
                    node.data?.data?.nodeId || // Alternativa 2 (dados aninhados)
                    (node.type && findNodeIdByType(node.type)); // Fallback por tipo
      
      if (nodeId) {
        loadNodeDefinition(nodeId);
      } else if (node.data && node.id) {
        createFallbackNodeDefinition(node);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id]);

  // Função para encontrar o nodeId com base no tipo
  const findNodeIdByType = (nodeType: string): string | null => {
    // Mapeamento de tipos de nós para IDs padrão
    const typeToIdMap: Record<string, string> = {
      'webhook_trigger': '680c6723-6222-4c0d-b4a7-0c3383b56472',
      'rest': '78fb5940-a842-484e-b8dc-f4e68fb830b7',
      'mapping': 'b9f98267-d875-4b91-83c0-14f7c1e48ad7',
      'rag_assistant': '5c84357d-f08c-4e17-ab9b-d1f1fd1e41bb',
      'end': '0a978529-a673-47d1-9f69-c40f14fd5cd6'
    };
    
    return typeToIdMap[nodeType] || null;
  };
  
  // Função para criar uma definição de nó fallback quando a API falhar
  const createFallbackNodeDefinition = (node: WorkflowNode) => {
    // Extrair configurações do próprio nó
    const config = node.config || node.data?.config || {};
    const workflowConfig = node.data?.workflowConfig || {};
    
    // Criar campos com base nas configurações existentes
    const wfConfig: Record<string, unknown> = {};
    const fallbackSettings: Record<string, unknown> = {
      workflowConfig: wfConfig
    };

    // Processar configurações do nó
    Object.entries(config).forEach(([key, value]) => {
      if (key !== 'title') {
        const typedValue = value as Record<string, unknown> | null;
        wfConfig[key] = {
          type: typeof value === 'object' && value !== null ? (typedValue as Record<string, unknown>).type || 'string' : 'string',
          value: typeof value === 'object' && value !== null ? (typedValue as Record<string, unknown>).value : value
        };
      }
    });

    // Adicionar as configurações de workflowConfig, se existirem
    Object.entries(workflowConfig).forEach(([key, value]) => {
      if (key !== 'title') {
        wfConfig[key] = value;
      }
    });
    
    // Definir título do nó
    fallbackSettings.title = node.data?.title || node.data?.label || 'Nó sem título';
    
    // Definir a definição do nó
    setNodeDefinition({
      id: node.id,
      name: node.data?.name || node.type,
      type: node.type,
      settings: fallbackSettings
    });
  };

  // Criar schema do formulário dinamicamente baseado nas configurações do node
  const formSchema = useMemo(() => {
    const schemaFields: Record<string, z.ZodTypeAny> = {
      title: z.string().min(1, t('common.required')),
    };

    if (!nodeDefinition) {
      return z.object(schemaFields);
    }

    // Determinar a fonte dos campos com base na estrutura
    let fields: NodeField[] = [];
    if (nodeDefinition.settings) {
      fields = extractFields(nodeDefinition.settings) as NodeField[];
    } else if (nodeDefinition.workflowConfig) {
      fields = extractFields(nodeDefinition) as NodeField[];
    }


    fields.forEach((field: NodeField) => {
      const key = field.name || field.key;
      if (key) {
        schemaFields[key] = generateFieldSchema(field as Parameters<typeof generateFieldSchema>[0]);
      }
    });

    return z.object(schemaFields);
  }, [nodeDefinition, t]);

  // Calcular valores padrão do formulário
  const defaultValues = useMemo(() => {
    if (!node || !nodeDefinition) return { title: '' };

    // Determinar a fonte dos campos com base na estrutura
    let fields: NodeField[] = [];
    if (nodeDefinition.settings) {
      fields = extractFields(nodeDefinition.settings) as NodeField[];
    } else if (nodeDefinition.workflowConfig) {
      fields = extractFields(nodeDefinition) as NodeField[];
    }

    const values = fields.reduce((acc: Record<string, unknown>, field: NodeField) => {
      const key = field.name || field.key;
      if (key) {
        // Verificar diferentes lugares onde o valor pode estar
        let value;
        const configEntry = node.config?.[key] as { value?: unknown; type?: string } | undefined;
        if (configEntry?.value !== undefined) {
          // Estrutura node.config[key].value
          value = configEntry.value;
        } else if (node.config?.[key] !== undefined && typeof node.config[key] !== 'object') {
          // Estrutura node.config[key] diretamente (quando não é um objeto)
          value = node.config[key];
        } else if (node.data?.workflowConfig?.[key]?.value !== undefined) {
          // Nova estrutura com workflowConfig
          value = node.data.workflowConfig[key].value;
        } else {
          // Valor padrão do campo
          value = field.default ?? '';
        }

        acc[key] = value;
      }
      return acc;
    }, {});

    // Configurar o título a partir de várias possíveis fontes
    values.title = node.data.title || node.data.label || node.config?.title || '';
    
    return values;
  }, [node, nodeDefinition]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // Resetar o form quando o node ou suas configurações mudarem
  useEffect(() => {
    if (!node || !nodeDefinition) return;

    const values = Object.entries(node.config || {}).reduce((acc: Record<string, unknown>, [key, value]) => {
      if (typeof value === 'object' && value !== null) {
        const objValue = value as { value?: unknown };
        acc[key] = objValue.value !== undefined ? objValue.value : value;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});

    values.title = node.config?.title || node.data.label;
    
    // Preservar os valores atuais do formulário para campos que não estão nos valores iniciais
    // Isso evita que campos que se tornam visíveis percam seus valores
    const currentValues = form.getValues();
    const mergedValues = { ...currentValues, ...values };
    
    form.reset(mergedValues);
    
    // Atualizar também o estado formValues para garantir consistência
    setFormValues(prev => ({
      ...prev,
      ...mergedValues
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node?.id, nodeDefinition]);

  const loadNodeDefinition = async (nodeId: string) => {
    if (!nodeId) {
      return;
    }

    try {
      const { data: nodeData, error } = await WorkflowBaseService.getNode(nodeId);

      if (error) {
        // Em caso de erro, não lançamos uma exceção, mas tentamos criar uma definição fallback
        if (node) {
          createFallbackNodeDefinition(node);
        }
        return;
      }
      
      // Validar e processar a estrutura da definição do nó
      if (!nodeData) {
        // Em caso de definição vazia, tentar criar uma definição fallback
        if (node) {
          createFallbackNodeDefinition(node);
        }
      } else {
        // Verificar se temos a nova estrutura (success, data) ou a antiga (settings diretamente)
        if (nodeData.success && nodeData.data) {
          // Nova estrutura da API com campos success e data
          setNodeDefinition(nodeData.data as NodeDefinition);
        } else if (nodeData.workflowConfig) {
          // Nova estrutura com workflowConfig, settings e workflowData
          setNodeDefinition({
            settings: nodeData // Passamos o nodeData inteiro como settings para que o extractFields possa acessar workflowConfig
          });
        } else if (nodeData.settings) {
          // Estrutura antiga com settings diretamente
          setNodeDefinition(nodeData);
        } else if (nodeData.id) {
          // Tentativa para lidar com a estrutura que você mostrou no exemplo
          const wfConfig: Record<string, unknown> = (nodeData.workflowConfig as Record<string, unknown>) || {};
          const processedData: NodeDefinition = {
            id: String(nodeData.id),
            name: nodeData.name as string | undefined,
            type: nodeData.type as string | undefined,
            settings: {
              workflowConfig: wfConfig
            }
          };

          // Se a definição tem workflowConfig, usá-la diretamente
          if (nodeData.workflowConfig) {
            processedData.settings!.workflowConfig = nodeData.workflowConfig as Record<string, unknown>;
          }
          // Se a definição tem workflowData, usar seus dados como campos
          else if (nodeData.workflowData) {
            // Converter objetos em configurações estruturadas
            const wd = nodeData.workflowData as Record<string, unknown>;
            Object.entries(wd).forEach(([key, value]) => {
              if (key !== 'title' && typeof value !== 'function') {
                wfConfig[key] = {
                  type: typeof value,
                  value: value
                };
              }
            });
          }

          setNodeDefinition(processedData);
        } else {
          // Tentativa de adaptar a estrutura desconhecida
          setNodeDefinition({
            id: nodeId,
            name: nodeData.name as string | undefined || 'Desconhecido',
            type: nodeData.type as string | undefined || 'unknown',
            settings: {
              fields: [],
              // Tentar criar uma estrutura que extractFields possa processar
              workflowConfig: (nodeData.workflowData as Record<string, unknown>) || nodeData
            }
          });
        }
      }
    } catch (error) {
      console.error('[NodeConfigDialog] Erro ao carregar definição do nó:', error);
      // Em caso de erro, tentar criar uma definição fallback
      if (node) {
        createFallbackNodeDefinition(node);
      }
      
      toast({
        title: t('error').charAt(0).toUpperCase() + t('error').slice(1),
        description: t('common.errors.tryAgain'),
        variant: "destructive",
      });
    }
  };

  // Função para buscar dados de entrada das edges
  const fetchEdgeInputData = useCallback(async () => {
    if (!node?.data?.workflowId) return;

    try {
      const { data: workflow, error } = await WorkflowBaseService.getWorkflow(node.data.workflowId);

      if (error) throw error;
      
      // Lidar com diferentes estruturas de resposta possíveis
      let workflowSettings: Record<string, unknown>;
      
      const workflowData = workflow?.data as Record<string, unknown> | undefined;
      if (workflowData?.settings) {
        // Nova estrutura: { data: { settings: { ... } } }
        workflowSettings = workflowData.settings as Record<string, unknown>;
      } else if (workflow?.settings) {
        // Estrutura anterior: { settings: { ... } }
        workflowSettings = workflow.settings as Record<string, unknown>;
      } else {
        console.error('[NodeConfigDialog] Estrutura de workflow inesperada:', workflow);
        workflowSettings = {};
      }

      const edges = (workflowSettings?.edges as WorkflowEdge[] | undefined) || [];
      const incomingEdges = edges.filter((edge: WorkflowEdge) => edge.target === node.id);

      // Buscar os dados de input de todas as edges de entrada
      const nodeInputs = incomingEdges.reduce((acc: Record<string, unknown>, edge: WorkflowEdge) => {
        // input may be at edge.input (top-level) or edge.data.input depending on save format
        let input: unknown = edge.input ?? (edge.data as Record<string, unknown> | undefined)?.input;

        // Se input for uma string, tenta fazer o parse para objeto
        if (typeof input === 'string') {
          try {
            input = JSON.parse(input);
          } catch (e) {
            input = { input };
          }
        }

        // Se o input for um array, usar o primeiro item
        if (Array.isArray(input)) {
          input = input[0];
        }

        // Garantir que o input tenha a estrutura correta
        if (input === null || input === undefined) {
          input = {};
        } else if (typeof input !== 'object') {
          input = { value: input };
        }

        return { ...acc, ...(input as Record<string, unknown>) };
      }, {});

      setInputData(nodeInputs);

      // Atualizar o formulário com os novos dados de input
      if (Object.keys(nodeInputs).length > 0) {
        const currentValues = form.getValues();
        const newValues = { ...currentValues };

        // Atualizar o input no formulário
        newValues.input = nodeInputs;

        form.reset(newValues);
      }
    } catch (error) {
      toast({
        title: t('error').charAt(0).toUpperCase() + t('error').slice(1),
        description: t('common.errorDescription'),
        variant: 'destructive',
      });
    }
  }, [node, form, t]);

  // Watch for form value changes to trigger re-render for visibility conditions
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const initialFormValuesRef = useRef<Record<string, unknown> | null>(null);
  
  // Watch for changes in form values to update visibility conditions and notify parent
  useEffect(() => {
    const subscription = form.watch((value) => {
      // Only update formValues with defined values to prevent undefined values
      // from overriding existing values when fields become invisible
      const cleanedValues = Object.entries(value || {}).reduce<Record<string, unknown>>((acc, [key, val]) => {
        if (val !== undefined) {
          acc[key] = val;
        }
        return acc;
      }, {});
      
      setFormValues(prev => ({
        ...prev,
        ...cleanedValues
      }));
      
      // Verificar se houve alterações comparando com valores iniciais
      if (initialFormValuesRef.current && node) {
        const hasChanges = Object.keys(cleanedValues).some(key => {
          const initialValue = initialFormValuesRef.current?.[key];
          const currentValue = cleanedValues[key];
          return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
        });
        
        if (hasChanges && !hasLocalChanges) {
          setHasLocalChanges(true);
          
          // Atualizar o node com a config atual para disparar a detecção de alterações no WorkflowEditor
          setNodes(nodes => nodes.map(n => {
            if (n.id === node.id) {
              return {
                ...n,
                data: {
                  ...n.data,
                  _hasLocalChanges: true // Flag para indicar alterações locais
                }
              };
            }
            return n;
          }));
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form, node, hasLocalChanges, setNodes]);
  
  // Salvar valores iniciais do formulário quando o node é carregado
  useEffect(() => {
    if (node && nodeDefinition && !initialFormValuesRef.current) {
      const currentValues = form.getValues();
      initialFormValuesRef.current = { ...currentValues };
      setHasLocalChanges(false);
    }
  }, [node, nodeDefinition, form]);
  
  // Reset quando o diálogo fecha
  useEffect(() => {
    if (!open) {
      initialFormValuesRef.current = null;
      setHasLocalChanges(false);
    }
  }, [open]);

  // Renderizar os campos do formulário
  const renderField = (key: string, field: NodeField) => {
    if (!field.type) {
      return null;
    }

    return createField({
      field: field as unknown as BaseFieldProps['field'],
      name: key,
      control: form.control,
      renderField,
      renderArrayField: renderArrayField,
      formValues: formValues as Record<string, unknown>, // Use watched form values for visibility conditions
      // Use only keys from current canvas config so dropdown reflects live canvas state.
      // inputData (last execution) is only used for the NodeInput display panel below.
      nodeInputData: Object.fromEntries(derivedInputKeys.map(k => [k, ''])),
    });
  };

  // Renderizar campos de array
  const renderArrayField = (key: string, field: NodeField) => {
    return (
      <FormField
        key={key}
        control={form.control}
        name={key}
        render={({ field: formField }) => {
          // Garantir que o valor seja sempre um array
          const value = Array.isArray(formField.value) ? formField.value : [];
          
          const itemsWithIds = value.map((item: Record<string, unknown>) => {
            if (!item._id) {
              return { ...item, _id: crypto.randomUUID() };
            }
            return item;
          });

          if (JSON.stringify(value) !== JSON.stringify(itemsWithIds)) {
            form.setValue(key, itemsWithIds);
          }

          form.register(key);

          const currentValue = Array.isArray(form.getValues(key)) ? form.getValues(key) : [];

          return (
            <FormItem className="w-full">
              <FormLabel>{field.label}</FormLabel>
              <FormDescription>{field.description}</FormDescription>
              <div className="space-y-3">
                {currentValue.map((item: Record<string, unknown>, index: number) => (
                  <div key={item._id as string} className="relative flex flex-col gap-3 p-4 border rounded-lg bg-background">
                    <RemoveItemButton
                      onRemove={() => {
                        const newValue = currentValue.filter((_: unknown, i: number) => i !== index);
                        form.setValue(key, newValue);
                      }}
                    />
                    <ArrayFieldItemForm
                      field={field}
                      index={index}
                      baseKey={key}
                      control={form.control}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newItem = field.items!.fields.reduce((acc: Record<string, unknown>, subField: { name: string; default?: unknown; [key: string]: unknown }) => {
                      acc[subField.name] = subField.default || '';
                      return acc;
                    }, { _id: crypto.randomUUID() } as Record<string, unknown>);
                    
                    const currentItems = form.getValues(key) || [];
                    
                    form.setValue(key, [...currentItems, newItem]);
                  }}
                  className="mt-2"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('agents.workflow.nodeConfig.mapping.addItem')}
                </Button>
              </div>
            </FormItem>
          );
        }}
      />
    );
  };

  // Renderizar os campos do formulário
  const renderFields = () => {
    // Determinar a fonte dos campos com base na estrutura
    let fields: NodeField[] = [];
    if (nodeDefinition?.settings) {
      fields = extractFields(nodeDefinition.settings) as NodeField[];
    } else if (nodeDefinition?.workflowConfig) {
      fields = extractFields(nodeDefinition) as NodeField[];
    } else if (!nodeDefinition) {
      return null;
    }

    if (fields.length === 0) {
      return (
        <div className="text-sm text-muted-foreground text-center py-4">
          {t('agents.workflow.node.config.noFields')}
        </div>
      );
    }

    return fields.map((field: NodeField) => {
      const key = field.name || field.key;
      if (!key) {
        return null;
      }
      return renderField(key, field);
    }).filter(Boolean);
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (!node) return;

    try {
      setIsSaving(true); // Ativar estado de salvamento
      
      // Capturar valores dinâmicos da mesma forma que no teste
      const cronValue = form.getValues('cron') || form.watch('cron');
      const timezoneValue = form.getValues('timezone') || form.watch('timezone');
      
      const enhancedValues: Record<string, unknown> = {
        ...values,
        cron: cronValue || '0 9 * * *', // Valor padrão se não encontrar
        timezone: timezoneValue || 'America/Sao_Paulo' // Valor padrão se não encontrar
      };

      // Remover campos que são apenas visuais e não devem ir para o backend
      const { schedule_builder: _schedule_builder, ...cleanValues } = enhancedValues;
      
      // Usar os valores limpos no lugar dos values originais
      values = cleanValues;
      
      // Preparar configurações do node
      const nodeConfig: Record<string, unknown> = {
        title: values.title // Colocar o título diretamente no config
      };

      // Adicionar os outros campos ao config com formato esperado pelo backend
      Object.entries(values).forEach(([key, value]) => {
        if (key !== 'title') {
          // Tratar valores undefined ou null como string vazia
          if (value === undefined || value === null || value === '') {
            nodeConfig[key] = '';
          } else if (typeof value === 'object') {
            if (Array.isArray(value)) {
              // Para arrays, guardar explicitamente o tipo e o valor
              nodeConfig[key] = {
                value: value,
                type: 'array'
              };
            } else if (Object.keys(value as Record<string, unknown>).length === 0) {
              // Objetos vazios {} tratados como string vazia
              nodeConfig[key] = '';
            } else {
              // Para outros objetos complexos
              nodeConfig[key] = value;
            }
          } else {
            // Para tipos primitivos, usar o formato { value, type }
            nodeConfig[key] = { value, type: typeof value };
          }
        }
      });


      // Atualizar o node com a nova configuração
      const updatedNode = {
        ...node,
        config: nodeConfig, // Colocar config diretamente no node
        data: {
          ...node.data,
          label: values.title, // Atualizar o label no data também para garantir que a UI reflita o título
          title: values.title  // Atualizar o título no data também
        }
      };


      // Atualizar o node no workflow (apenas estado local)
      setNodes((nodes) =>
        nodes.map((n) => {
          if (n.id === node.id) {
            return updatedNode;
          }
          return n;
        })
      );

      // NÃO salvar automaticamente no banco de dados
      // As alterações serão salvas apenas quando o usuário clicar em "Publicar"
      // O WorkflowEditor notificará o AgentSettings sobre as alterações
      
      setIsSaving(false);
      
      // Fechar o dialog após salvar
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving node config:', error);
      toast({
        title: t('error').charAt(0).toUpperCase() + t('error').slice(1),
        description: t('common.messages.errorDescription'),
        variant: "destructive",
      });
      setIsSaving(false); // Desativar estado de salvamento
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Função para verificar se o node tem conexões de entrada
  const hasInputConnections = useCallback(() => {
    if (!node) return false;
    const edges = getEdges();
    return edges.some(edge => edge.target === node.id);
  }, [node, getEdges]);

  // Buscar dados de entrada quando o diálogo abrir ou o node mudar
  useEffect(() => {
    if (open && node) {
      fetchEdgeInputData();
    }
  }, [open, node, fetchEdgeInputData]);

  // Atualizar dados quando houver mudanças nos edges
  useEffect(() => {
    const handleEdgeChange = () => {
      if (open && node) {
        fetchEdgeInputData();
      }
    };

    window.addEventListener('updateEdgeInput', handleEdgeChange);
    window.addEventListener('workflowTestResult', handleEdgeChange);

    return () => {
      window.removeEventListener('updateEdgeInput', handleEdgeChange);
      window.removeEventListener('workflowTestResult', handleEdgeChange);
    };
  }, [open, node, fetchEdgeInputData]);

  if (!node || !nodeDefinition) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[1400px] h-[720px] flex flex-col">
        <DialogHeader className="pb-4">
          <DialogTitle>
            {isEditingTitle ? (
              <Input
                ref={titleInputRef}
                value={nodeTitle}
                onChange={(e) => setNodeTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onBlur={handleSaveTitle}
                className="h-7 w-[300px]"
              />
            ) : (
              <div
                className="flex items-center gap-2 cursor-pointer group"
                onClick={handleTitleEdit}
              >
                <span>{nodeTitle || t('agents.workflow.node.untitled')}</span>
                <Pencil className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
          </DialogTitle>
          <DialogDescription>
            {nodeDefinition?.description || t('agents.workflow.node.config.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-row flex-1 min-h-0">
          {hasInputConnections() && (
            <NodeInput
              node={node}
              inputData={inputData}
            />
          )}
          <div className="flex-1 overflow-y-auto pr-4">
            <Form {...form}>
              <form 
                onSubmit={(e) => {
                  e.preventDefault();
                  
                  // Se houver erros de validação, exibir toast
                  if (Object.keys(form.formState.errors).length > 0) {
                    toast({
                      title: t("workflow.nodeConfig.validationError"),
                      description: t("workflow.nodeConfig.validationErrorDescription"),
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  // Chamar handleSubmit com os valores atuais do form
                  handleSubmit(form.getValues());
                }} 
                className="space-y-4 px-6"
              >
                {renderFields()}
                <DialogFooter className="flex justify-between items-center gap-4 mt-4 px-0">
                  <Button 
                    type="submit" 
                    disabled={isSaving || isTesting}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('agents.workflow.saving')}
                      </>
                    ) : (
                      t('agents.workflow.save')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </div>
          <NodeTesting
            node={node}
            formValues={(() => {
              const baseValues = form.getValues();
              
              // Tentar capturar valores dinâmicos de diferentes formas
              const cronValue = form.getValues('cron') || form.watch('cron');
              const timezoneValue = form.getValues('timezone') || form.watch('timezone');
              
              const enhancedValues: Record<string, unknown> = {
                ...baseValues,
                cron: cronValue || form.watch('cron') || '0 9 * * *',
                timezone: timezoneValue || form.watch('timezone') || 'America/Sao_Paulo'
              };

              const { schedule_builder: _schedule_builder2, ...finalValues } = enhancedValues;
              
              return finalValues;
            })()}
            isTesting={isTesting}
            setIsTesting={setIsTesting}
            testOutput={testOutput}
            setTestOutput={setTestOutput}
            testStatus={testStatus}
            setTestStatus={setTestStatus}
            nodeOutput={nodeOutput}
            setNodeOutput={setNodeOutput}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default NodeConfigDialog;
